/**
 * Audit Log Service – insert + query audit_logs table
 * Phase 27 – Enterprise metadata: device, IP, session, browser, OS
 */
import { supabase, handleError, type ServiceResult, type PaginatedResult, getPaginationRange } from './base.service';

export interface AuditLogRow {
  id: string;
  project_id?: string;
  actor_user_id?: string;
  action_code: string;
  module_code: string;
  entity_type: string;
  entity_id?: string;
  summary: string;
  before_data?: Record<string, unknown>;
  after_data?: Record<string, unknown>;
  created_at: string;
  // Enterprise metadata
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  country_code?: string;
  request_id?: string;
  // joined
  actor?: { full_name: string; email: string };
}

export interface AuditLogFilters {
  project_id?: string;
  module_code?: string;
  action_code?: string;
  actor_user_id?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateAuditLogInput {
  project_id?: string;
  company_id?: string;
  action_code: string;
  module_code: string;
  entity_type: string;
  entity_id?: string;
  summary: string;
  before_data?: Record<string, unknown>;
  after_data?: Record<string, unknown>;
}

// ─── Device metadata detection ──────────────────────────────

function detectDeviceType(ua: string): string {
  if (/mobile|android|iphone|ipad|ipod/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
}

function detectBrowser(ua: string): string {
  if (/edg\//i.test(ua)) return 'Edge';
  if (/chrome/i.test(ua) && !/chromium/i.test(ua)) return 'Chrome';
  if (/firefox/i.test(ua)) return 'Firefox';
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
  if (/opr\//i.test(ua)) return 'Opera';
  return 'Unknown';
}

function detectOS(ua: string): string {
  if (/windows nt/i.test(ua)) return 'Windows';
  if (/mac os x/i.test(ua)) return 'macOS';
  if (/linux/i.test(ua) && !/android/i.test(ua)) return 'Linux';
  if (/android/i.test(ua)) return 'Android';
  if (/ios|iphone|ipad|ipod/i.test(ua)) return 'iOS';
  return 'Unknown';
}

function generateRequestId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function collectDeviceMetadata(): {
  user_agent: string;
  device_type: string;
  browser: string;
  os: string;
  request_id: string;
} {
  const ua = navigator.userAgent;
  return {
    user_agent: ua,
    device_type: detectDeviceType(ua),
    browser: detectBrowser(ua),
    os: detectOS(ua),
    request_id: generateRequestId(),
  };
}

const auditService = {
  /** List audit logs with actor info */
  async list(filters: AuditLogFilters = {}): Promise<PaginatedResult<AuditLogRow>> {
    try {
      const { from, to } = getPaginationRange(filters.page, filters.pageSize ?? 30);
      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          actor:user_profiles!actor_user_id(full_name, email)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (filters.project_id) query = query.eq('project_id', filters.project_id);
      if (filters.module_code && filters.module_code !== 'all') query = query.eq('module_code', filters.module_code);
      if (filters.action_code && filters.action_code !== 'all') query = query.eq('action_code', filters.action_code);
      if (filters.actor_user_id) query = query.eq('actor_user_id', filters.actor_user_id);
      if (filters.date_from) query = query.gte('created_at', filters.date_from);
      if (filters.date_to) query = query.lte('created_at', filters.date_to);

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: (data ?? []) as AuditLogRow[], count: count ?? 0, error: null };
    } catch (err) {
      return { data: [], count: 0, error: handleError(err) };
    }
  },

  /** Manually insert audit log with full enterprise metadata */
  async log(input: CreateAuditLogInput): Promise<ServiceResult<void>> {
    try {
      const meta = collectDeviceMetadata();
      // Get session and profile to detect company_id
      const { data: { session } } = await supabase.auth.getSession();
      
      let companyId = input.company_id;
      if (!companyId && session?.user?.id) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('id', session.user.id)
          .single();
        if (profile) companyId = profile.company_id;
      }

      const payload = {
        ...input,
        ...meta,
        company_id: companyId,
        session_id: session?.access_token
          ? session.access_token.slice(-16)
          : null,
      };
      const { error } = await supabase.from('audit_logs').insert(payload);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Log user login event with full metadata */
  async logLogin(userId: string): Promise<ServiceResult<void>> {
    return auditService.log({
      action_code: 'LOGIN',
      module_code: 'auth',
      entity_type: 'user',
      entity_id: userId as unknown as undefined,
      summary: 'User logged in',
    });
  },
};

export default auditService;
